import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Prism from 'prismjs';
import { ChromePicker } from 'react-color';

const ShadowDiv = styled.div`
  transition: all 150ms ease-in-out;
  box-shadow: ${({ offsetX, offsetY, blur, spread, shadowColor }) =>
    `${offsetX}px ${offsetY}px ${blur}px ${spread}px rgba(${shadowColor.r},${shadowColor.g},${shadowColor.b},${shadowColor.a})`};
`;

const inputClassName =
  'w-20 h-12 text-xl pl-4 shadow-lg rounded text-white bg-gray-900 border-purple-800 border-b-4';

const Shadow = ({ dark }) => {
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(4);
  const [blur, setBlur] = useState(6);
  const [spread, setSpread] = useState(0);
  const [shadowColor, setShadowColor] = useState({
    r: '35',
    g: '44',
    b: '66',
    a: '0.40'
  });

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const [borderCode, setBorderCode] = useState(
    `${offsetX}px ${offsetY}px ${blur}px ${spread}px rgba(${shadowColor.r},${shadowColor.g},${shadowColor.b},${shadowColor.a})`
  );

  useEffect(() => {
    if (offsetX < 0) setOffsetX(0);
    if (offsetY < 0) setOffsetY(0);
    if (blur < 0) setBlur(0);
    if (spread < 0) setSpread(0);

    setBorderCode(
      `${offsetX}px ${offsetY}px ${blur}px ${spread}px rgba(${shadowColor.r},${shadowColor.g},${shadowColor.b},${shadowColor.a})`
    );
  }, [offsetX, offsetY, blur, spread, shadowColor]);

  useEffect(() => {
    if (showCode) setTimeout(() => Prism.highlightAll(), 0);
  }, [offsetX, offsetY, blur, spread, showCode, shadowColor]);

  const [copySuccess, setCopySuccess] = useState(false);
  const cssCode = useRef(null);

  const copyText = element => {
    let range, selection;

    if (document.body.createTextRange) {
      range = document.body.createTextRange();
      range.moveToElementText(element);
      range.select();
    } else if (window.getSelection) {
      selection = window.getSelection();
      range = document.createRange();
      range.selectNodeContents(element);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    document.execCommand('copy');
  };

  const copyCode = e => {
    copyText(cssCode.current);

    e.target.focus();

    setCopySuccess(true);
    setShowCode(false);
  };

  useEffect(() => {
    if (copySuccess) setTimeout(() => setCopySuccess(!copySuccess), 3000);
  }, [copySuccess]);

  const closeColorPicker = () => {
    if (showColorPicker === true) setShowColorPicker(false);
  };

  return (
    <section class="flex-1 flex flex-col mb-10 text-center items-center h-full" style={{marginTop: window.innerWidth > 770 ? "3%" : "12%"}}>
      <h1
        className={`text-5xl w-full font-bold tracking-wider select-none ${
          dark ? 'text-white' : 'text-indigo-900'
        }`}
      >
        Box Shadow
      </h1>

      <ShadowDiv
        spread={spread}
        offsetY={offsetY}
        blur={blur}
        offsetX={offsetX}
        shadowColor={shadowColor}
        className="w-56 h-56 mt-20 block relative border rounded border-indigo-500 bg-indigo-600"
      >
        <div
          className="absolute h-full flex flex-col justify-between"
          style={{ right: '-80%', top: '-40%', height: '180%' }}
        >
          <label
            htmlFor="offsetY"
            className={`${dark ? 'text-white' : 'text-indigo-900'} mb-1`}
          >
            Offset Y
          </label>
          <input
            className={inputClassName}
            id="offsetY"
            type="number"
            value={offsetX}
            onChange={e => setOffsetX(e.target.value)}
          />

          <label
            htmlFor="offsetX"
            className={`${dark ? 'text-white' : 'text-indigo-900'} mb-1`}
          >
            Offset X
          </label>
          <input
            className={inputClassName}
            id="offsetX"
            type="number"
            value={offsetY}
            onChange={e => setOffsetY(e.target.value)}
          />

          <label
            htmlFor="blur"
            className={`${dark ? 'text-white' : 'text-indigo-900'} mb-1`}
          >
            Blur
          </label>
          <input
            className={inputClassName}
            id="blur"
            type="number"
            value={blur}
            onChange={e => setBlur(e.target.value)}
          />

          <label
            htmlFor="spread"
            className={`${dark ? 'text-white' : 'text-indigo-900'} mb-1`}
          >
            Spread
          </label>
          <input
            className={inputClassName}
            id="spread"
            type="number"
            value={spread}
            onChange={e => setSpread(e.target.value)}
          />

          <label className={`${dark ? 'text-white' : 'text-indigo-900'} mb-1`}>
            Color
          </label>
          <button
            className="w-20 h-12 text-xl py-1 px-2 shadow-lg rounded text-white bg-gray-900 border-purple-800 border-b-4"
            onClick={() => setShowColorPicker(true)}
          >
            <div className="rounded h-8 border border-red-500 text-red-600 bg-red-600">
              {' '}
            </div>
          </button>

          {showColorPicker && (
            <>
              <ChromePicker
                className="absolute bottom-0  ml-24 z-10"
                color={shadowColor}
                onChangeComplete={color => setShadowColor(color.rgb)}
              />
              <div
                className="fixed top-0 right-0 left-0 bottom-0 z-0"
                onClick={() => closeColorPicker()}
              />
            </>
          )}
        </div>
      </ShadowDiv>
      {!showCode && (
        <button
          className={`mt-40 z-50 shadow text-white font-bold py-2 px-4 rounded ${
            dark
              ? 'bg-pink-600 hover:bg-pink-700'
              : 'bg-pink-700 hover:bg-pink-600'
          }`}
          onClick={() => setShowCode(!showCode)}
        >
          Get CSS Code
        </button>
      )}
      {showCode && (
        <div
          className="fixed"
          style={{
            bottom: '18%'
          }}
        >
          <pre className=" shadow-md bottom-0 language-css">
            <span
              onClick={() => setShowCode(!showCode)}
              className="absolute right-0 top-0 px-3 py-4 rounded cursor-pointer select-none focus:bg-blue-200"
            >
              &#10006;
            </span>
            <code ref={cssCode}>
              {`.shadowedBox {
  -webkit-box-shadow: ${borderCode};
  -moz-box-shadow: ${borderCode};
  box-shadow: ${borderCode}; 
}`}
            </code>
          </pre>
          <button
            className="w-full bg-indigo-600 rounded shadow-md py-1 text-white"
            onClick={e => copyCode(e, cssCode)}
          >
            Copy to Clipboard
          </button>
        </div>
      )}
      {copySuccess > 0 && (
        <span className="fixed shadow-md px-6 py-4 text-white text-xl tracking-wider bg-green-500 rounded-lg">
          Successfully Copied!
        </span>
      )}
    </section>
  );
};

export default Shadow;
